import { useState } from "react";
import {
  MAX_INCREMENT_SECONDS,
  MAX_INITIAL_SECONDS,
  type TimeControl,
} from "@chess/shared";

const MAX_MINUTES = MAX_INITIAL_SECONDS / 60;

const PRESETS: { label: string; value: TimeControl | null }[] = [
  { label: "Unlimited", value: null },
  { label: "3 + 2", value: { initialSeconds: 180, incrementSeconds: 2 } },
  { label: "5 + 3", value: { initialSeconds: 300, incrementSeconds: 3 } },
  { label: "10 + 5", value: { initialSeconds: 600, incrementSeconds: 5 } },
];

export function describeTimeControl(control: TimeControl | null): string {
  if (!control) return "Unlimited";
  const minutes = control.initialSeconds / 60;
  const shown = Number.isInteger(minutes) ? minutes : (control.initialSeconds / 60).toFixed(1);
  return `${shown} + ${control.incrementSeconds}`;
}

export interface TimeControlPickerProps {
  value: TimeControl | null;
  onChange: (value: TimeControl | null) => void;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function TimeControlPicker({ value, onChange }: TimeControlPickerProps) {
  const [custom, setCustom] = useState(false);
  const [minutes, setMinutes] = useState(10);
  const [increment, setIncrement] = useState(5);

  function chooseCustom(nextMinutes: number, nextIncrement: number) {
    const safeMinutes = clamp(nextMinutes, 1, MAX_MINUTES);
    const safeIncrement = clamp(nextIncrement, 0, MAX_INCREMENT_SECONDS);
    setMinutes(safeMinutes);
    setIncrement(safeIncrement);
    onChange({ initialSeconds: safeMinutes * 60, incrementSeconds: safeIncrement });
  }

  const activePreset = custom
    ? null
    : PRESETS.find((preset) =>
        preset.value === null
          ? value === null
          : value !== null &&
            value.initialSeconds === preset.value.initialSeconds &&
            value.incrementSeconds === preset.value.incrementSeconds,
      );

  return (
    <fieldset className="picker">
      <legend className="field__label">Time control</legend>

      <div className="chips">
        {PRESETS.map((preset) => (
          <button
            key={preset.label}
            type="button"
            className={`chip-button${activePreset === preset ? " chip-button--on" : ""}`}
            aria-pressed={activePreset === preset}
            onClick={() => {
              setCustom(false);
              onChange(preset.value);
            }}
          >
            {preset.label}
          </button>
        ))}
        <button
          type="button"
          className={`chip-button${custom ? " chip-button--on" : ""}`}
          aria-pressed={custom}
          onClick={() => {
            setCustom(true);
            chooseCustom(minutes, increment);
          }}
        >
          Custom
        </button>
      </div>

      {custom ? (
        <div className="custom-time">
          <label className="field field--inline">
            <span className="field__label">Minutes</span>
            <input
              type="number"
              min={1}
              max={MAX_MINUTES}
              value={minutes}
              onChange={(event) => chooseCustom(Number(event.target.value), increment)}
            />
          </label>
          <label className="field field--inline">
            <span className="field__label">Increment (s)</span>
            <input
              type="number"
              min={0}
              max={MAX_INCREMENT_SECONDS}
              value={increment}
              onChange={(event) => chooseCustom(minutes, Number(event.target.value))}
            />
          </label>
        </div>
      ) : null}
    </fieldset>
  );
}
